/* eslint-disable no-restricted-syntax */
import {
  all, call, put, select, takeEvery,
} from 'redux-saga/effects';
import { request } from 'utils/request';
import { IRestApiResponse } from 'interfaces';
import { selectEnterprise } from 'base/core/selectors';

import {
  CourseTypes,
  DaysOfTheWeek,
  IAvailableTime,
  IClass,
  LiveClassTypes,
  SessionFrequencyTypes,
  SessionType,
} from 'pages/sessions/interface';
import { getOptionsFromEnum } from 'utils/data-utils';
import moment from 'moment';
import { selectAuthUser } from 'base/auth/selectors';
import { currencyFormat } from 'utils/formats';
import { enumerateDaysBetweenDates, getDateWithTime, getTimeStops } from 'utils/common-utils';
import { CategoryResourceType } from 'constants/sessions';
import API from './constants';
import {
  ICreateSessionForm,
  IFilterStateAction,
  ISessionDetailsAction,
  ISessionCreateFormStateAction,
  ISessionUpdateFormStateAction,
  ISessionFilterStateAction,
  ISessionMeta,
  BulkCancelSession,
  SessionRecordingShare,
} from './interface';

import { createSessionActions } from './slice';

export function* fetchGroupsGenerator({ payload }: IFilterStateAction): any {
  try {
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.GET_FETCH_GROUPS.path}?keyword=${payload.keyword}&page=${payload.page}&limit=${payload.limit}&groupType=${payload.type}`,
        method: API.GET_FETCH_GROUPS.method,
      },
      null,
      true,
    );
    if (response.statusCode === 200) {
      yield put(createSessionActions.fetchGroupsSucceeded(response.data));
    } else {
      yield put(createSessionActions.fetchGroupsFailed());
    }
  } catch (error) {
    yield put(createSessionActions.fetchGroupsFailed());
  }
}

export function* fetchSessionsGenerator({ payload }: ISessionFilterStateAction): any {
  const { isPrequisite, currentSessionId } = payload;
  try {
    const tenant = yield select(selectEnterprise);
    const expertIds = payload.expertIds && payload.expertIds.length > 0 ? payload.expertIds.toString() : '';
    let apiPath = {
      path: `${API.GET_FETCH_SESSIONS.path}/${tenant.id}/${expertIds}?keyword=${payload.keyword}&page=${payload.page}&limit=${payload.limit}&type=${payload.sessionType}&liveClassMeetingType=${payload.liveClassMeetingType}&status=${payload.status}&sortBy=${payload.sortBy}&publicOnly=${payload?.isPublicOnly ? 'true' : ''}&audienceType=${payload.audienceType ?? ''}&withoutDeleted=${payload.withoutDeleted ?? 'false'}&withoutSessionIds=${payload?.withoutSessionIds ?? ''}`,
      method: API.GET_FETCH_SESSIONS.method,
    };

    if (isPrequisite) {
      apiPath = {
        path: `${API.GET_PREQUISITE_SEARCH.path}/${tenant.id}/${expertIds}?keyword=${payload.keyword}&page=${payload.page}&limit=${payload.limit}&type=${payload.sessionType}&liveClassMeetingType=${payload.liveClassMeetingType}&status=${payload.status}&sortBy=${payload.sortBy}&publicOnly=${payload?.isPublicOnly ? 'true' : ''}&audienceType=${payload.audienceType ?? ''}&withoutDeleted=${payload.withoutDeleted ?? 'false'}&withoutSessionIds=${payload?.withoutSessionIds ?? ''}&currentSessionId=${currentSessionId}`,
        method: API.GET_PREQUISITE_SEARCH.method,
      };
    }

    const response: IRestApiResponse = yield call(
      request,
      apiPath,
      null,
      true,
    );
    if (response.statusCode === 200) {
      yield put(createSessionActions.fetchSessionsSucceeded(response.data));
    } else {
      yield put(createSessionActions.fetchSessionsFailed());
    }
  } catch (error) {
    yield put(createSessionActions.fetchSessionsFailed());
  }
}

const generateLiveClassSlots = async (data: ICreateSessionForm): Promise<any> => {
  const classes: IClass[] = [];

  if (data.sessionType === SessionType.liveClass) {
    const sessionStartDateTime = moment(`${data.frequency.startDate} ${data.frequency.startAt}`, 'YYYY-MM-DD HH:mm');
    const sessionEndDateTime = moment(`${data.frequency.endDate} ${data.frequency.endAt}`, 'YYYY-MM-DD HH:mm');
    let slots = data.numberOfSessions;

    if (data.frequency.frequency === SessionFrequencyTypes.oneTime) {
      const slotEndDateTime = moment(
        `${data.frequency.startDate} ${data.frequency.startAt}`,
        'YYYY-MM-DD HH:mm:ss',
      ).add(1, 'hours');
      classes.push({
        from: sessionStartDateTime.toISOString(),
        to: slotEndDateTime.toISOString(),
        date: sessionStartDateTime.toISOString(),
      });
    } else if (data.frequency.frequency === SessionFrequencyTypes.everyDay) {
      const now = sessionStartDateTime.clone();
      const endAtEveryDay = now.clone().add(1, 'hours');
      while (now.isSameOrBefore(sessionEndDateTime) && slots > 0) {
        classes.push({
          from: now.toISOString(),
          to: endAtEveryDay.toISOString(),
          date: now.toISOString(),
        });
        now.add(1, 'days');
        endAtEveryDay.add(1, 'days');
        slots -= 1;
      }
    } else if (data.frequency.frequency === SessionFrequencyTypes.everyWeek) {
      const now = sessionStartDateTime.clone();
      const endAtEveryWeek = now.clone().add(1, 'hours');
      const daysOfTheWeek = getOptionsFromEnum(DaysOfTheWeek);
      const dayOfWeek = daysOfTheWeek.filter(
        (dayOfTheWeek) => dayOfTheWeek.value === data.frequency.dayOfTheWeek,
      )[0];

      const weekdayId = dayOfWeek.id as number;

      if (now.isoWeekday() !== dayOfWeek.id) {
        const diff = (weekdayId + 7 - now.isoWeekday()) % 7;
        now.add(diff, 'days');
        endAtEveryWeek.add(diff, 'days');
      }

      while (now.isSameOrBefore(sessionEndDateTime) && slots > 0) {
        if (now.isoWeekday() === dayOfWeek.id) {
          classes.push({
            from: now.toISOString(),
            to: endAtEveryWeek.toISOString(),
            date: now.toISOString(),
          });
          slots -= 1;
        }

        now.add(1, 'week').day(dayOfWeek.id);
        endAtEveryWeek.add(1, 'week').day(dayOfWeek.id);
      }
    } else if (data.frequency.frequency === SessionFrequencyTypes.everyMonth) {
      const now = sessionStartDateTime.clone();
      const endAtEveryMonth = now.clone().add(1, 'hours');
      const dayOfMonth = Number(data.frequency.dayOfTheMonth);
      while (now.isSameOrBefore(sessionEndDateTime) && slots > 0) {
        if (now.date() !== dayOfMonth) {
          if (now.month() === sessionStartDateTime.month()) {
            now.add(0, 'month').date(dayOfMonth);
            endAtEveryMonth.add(0, 'month').date(dayOfMonth);
          } else {
            now.add(1, 'month').date(dayOfMonth);
            endAtEveryMonth.add(1, 'month').date(dayOfMonth);
          }
          // eslint-disable-next-line no-continue
          continue;
        }
        classes.push({
          from: now.toISOString(),
          to: endAtEveryMonth.toISOString(),
          date: now.toISOString(),
        });
        now.add(1, 'month').date(dayOfMonth);
        endAtEveryMonth.add(1, 'month').date(dayOfMonth);
        slots -= 1;
      }
    }
  }

  return classes;
};

const generateLiveSessionSlots = async (
  sessionMeta: ISessionMeta,
  availableTimes: IAvailableTime[],
  sessionDuration: number,
): Promise<IClass[]> => {
  const classes: IClass[] = [];
  const dates = await enumerateDaysBetweenDates(
    `${sessionMeta.startDate} 00:00:00`,
    `${sessionMeta.endDate} 00:00:00`,
  );

  /*
   Create classes array by getting experts avalible dates with his avalible times
   and using the session Duration
  */
  for (const date of dates) {
    for (const avalibleTime of availableTimes) {
      // check if the expert is avalible on the currentDay
      if (avalibleTime.isAvailable && avalibleTime.day === moment(date).format('dddd')) {
        // if the expert is avalible get his avalible times for the day
        for (const time of avalibleTime.times) {
          // calculate the startTime and endTime based on the expert avalible time

          const startTime = getDateWithTime(time.from ?? '08:00:00', date);
          const endTime = getDateWithTime(time.to ?? '17:00:00', date);

          // generate slots for the available times  and update those slots to
          // available classes for the session
          const slots = getTimeStops(startTime, endTime, sessionDuration);
          // eslint-disable-next-line array-callback-return
          slots.map((slot: any) => {
            classes.push({
              from: slot.start,
              to: slot.end,
              date: slot.start,
            });
          });
        }
      }
    }
  }
  return classes;
};

function* assignCategories(resourceId: string, categories: string[]) {
  yield call(
    request,
    API.POST_ASSIGN_CATEGORIES,
    {
      resourceId,
      categoryId: categories,
      resourceType: CategoryResourceType.SESSION,
    },
  );
}

export function* createSessionGenerator({ payload }: ISessionCreateFormStateAction): any {
  try {
    const tenant = yield select(selectEnterprise);
    const authUser = yield select(selectAuthUser);
    let classes = [];
    if (payload.sessionType === SessionType.liveClass) {
      classes = yield call(generateLiveClassSlots, payload);
    } else if (payload.sessionType === SessionType.liveSession) {
      classes = yield call(
        generateLiveSessionSlots,
        payload.sessionMeta,
        payload.availableTimes,
        payload.sessionDuration,
      );
    }
    const userSettings = yield { ...payload.userSettings };
    if (payload.sessionType === SessionType.course) {
      if (payload.courseType === CourseTypes.lsm) {
        userSettings.allowUpload = false;
        userSettings.allowMessaging = false;
      } else if (payload.courseType === CourseTypes.interactive) {
        userSettings.allowUpload = true;
        userSettings.allowMessaging = true;
      }
    } else if (
      payload.sessionType === SessionType.liveClass
      && payload.sessionMeta.liveClassType === LiveClassTypes.streaming
    ) {
      userSettings.allowUpload = false;
      userSettings.allowMessaging = false;
    }

    const { categories, prerequisites } = payload;

    const response: IRestApiResponse = yield call(
      request,
      API.POST_CREATE_SESSION,
      {
        ...payload,
        userSettings,
        frequency: {
          startAt: moment(
            `${payload.frequency.startDate} ${payload.frequency.startAt}`,
            'YYYY-MM-DD HH:mm',
          ).toISOString(),
          endAt: moment(
            `${payload.frequency.endDate} ${payload.frequency.endAt}`,
            'YYYY-MM-DD HH:mm',
          ).toISOString(),
          startDate: moment(
            `${payload.frequency.startDate} ${payload.frequency.startAt}`,
            'YYYY-MM-DD HH:mm',
          ).toISOString(),
          endDate: moment(
            `${payload.frequency.endDate} ${payload.frequency.endAt}`,
            'YYYY-MM-DD HH:mm',
          ).toISOString(),
          dayOfTheWeek: payload.frequency.dayOfTheWeek,
          dayOfTheMonth: payload.frequency.dayOfTheMonth,
          frequency: payload.frequency.frequency,
          classes,
        },
        entId: tenant.id,
        isActive: authUser?.attributes?.isActive !== false,
        fee: Number(payload.fee).toFixed(2).toLocaleString(),
        discount: Number(payload.discount).toString(),
        subscriberFee: Number(payload.subscriberFee).toFixed(2).toLocaleString(),
        subscriberDiscount: Number(payload.subscriberDiscount).toString(),
        observerFee: Number(payload.observerFee).toFixed(2).toLocaleString(),
        observerDiscount: Number(payload.observerDiscount).toString(),
        subscribeObserverFee: Number(payload.subscribeObserverFee).toFixed(2).toLocaleString(),
        subscribeObserverDiscount: Number(payload.subscribeObserverDiscount).toString(),
        currency: authUser.attributes.currency,
        subscriberCurrency: authUser.attributes.currency,
        observerCurrency: authUser.attributes.currency,
        subscribeObserverCurrency: authUser.attributes.currency,
        classes,
        prerequisites: Array.isArray(prerequisites) ? prerequisites.map((i) => i.id) : undefined,
      },
      true,
    );
    if (response.statusCode === 201) {
      yield put(createSessionActions.createSessionSucceeded({
        ...response.data,
        moduleIndex: payload.moduleIndex,
      }));
      yield assignCategories(response.data?.session?._id, categories);
      yield payload.resolve(response.data.session);
    } else {
      yield put(createSessionActions.createSessionFailed());
      yield payload.reject();
    }
  } catch (error) {
    yield put(createSessionActions.createSessionFailed());
    yield payload.reject();
  }
}

export function* updateSessionGenerator({ payload }: ISessionUpdateFormStateAction): any {
  try {
    const tenant = yield select(selectEnterprise);
    const authUser = yield select(selectAuthUser);
    let classes = [];
    if (payload.data.sessionType === SessionType.liveClass) {
      classes = yield call(generateLiveClassSlots, payload.data);
    } else if (payload.data.sessionType === SessionType.liveSession) {
      classes = yield call(
        generateLiveSessionSlots,
        payload.data.sessionMeta,
        payload.data.availableTimes,
        payload.data.sessionDuration,
      );
    }
    const userSettings = yield { ...payload.data.userSettings };

    if (payload.data.sessionType === SessionType.course) {
      if (payload.data.courseType === CourseTypes.lsm) {
        userSettings.allowUpload = false;
        userSettings.allowMessaging = false;
      } else if (payload.data.courseType === CourseTypes.interactive) {
        userSettings.allowUpload = true;
        userSettings.allowMessaging = true;
      }
    }

    const { prerequisites } = payload.data;

    const response: IRestApiResponse = yield call(
      request,
      API.POST_UPDATE_SESSION,
      {
        id: payload.id,
        ...payload.data,
        sessionMeta: payload.data.sessionMeta,
        userSettings,
        frequency: {
          startAt: moment(
            `${payload.data.frequency.startDate} ${payload.data.frequency.startAt}`,
            'YYYY-MM-DD HH:mm',
          ).toISOString(),
          endAt: moment(
            `${payload.data.frequency.endDate} ${payload.data.frequency.endAt}`,
            'YYYY-MM-DD HH:mm',
          ).toISOString(),
          startDate: moment(
            `${payload.data.frequency.startDate} ${payload.data.frequency.startAt}`,
            'YYYY-MM-DD HH:mm',
          ).toISOString(),
          endDate: moment(
            `${payload.data.frequency.endDate} ${payload.data.frequency.endAt}`,
            'YYYY-MM-DD HH:mm',
          ).toISOString(),
          dayOfTheWeek: payload.data.frequency.dayOfTheWeek,
          dayOfTheMonth: payload.data.frequency.dayOfTheMonth,
          frequency: payload.data.frequency.frequency,
          classes,
        },
        entId: tenant.id,
        isActive: authUser?.attributes?.isActive !== false,
        fee: Number(payload.data.fee).toFixed(2).toLocaleString(),
        discount: Number(payload.data.discount).toString(),
        subscriberFee: Number(payload.data.subscriberFee).toFixed(2).toLocaleString(),
        subscriberDiscount: Number(payload.data.subscriberDiscount).toString(),
        observerFee: Number(payload.data.observerFee).toFixed(2).toLocaleString(),
        observerDiscount: Number(payload.data.observerDiscount).toString(),
        subscribeObserverFee: Number(payload.data.subscribeObserverFee).toFixed(2).toLocaleString(),
        subscribeObserverDiscount: Number(payload.data.subscribeObserverDiscount).toString(),
        currency: authUser.attributes.currency,
        subscriberCurrency: authUser.attributes.currency,
        observerCurrency: authUser.attributes.currency,
        subscribeObserverCurrency: authUser.attributes.currency,
        classes,
        prerequisites: Array.isArray(prerequisites) ? prerequisites.map((i) => i.id) : undefined,
      },
      true,
    );

    const { categories } = payload.data;

    if (response.statusCode === 201) {
      yield assignCategories(payload.id, categories);
      yield put(createSessionActions.updateSessionSucceeded({
        ...response.data,
        moduleIndex: payload.data.moduleIndex,
      }));
      yield payload.data.resolve();
    } else {
      yield put(createSessionActions.updateSessionFailed());
      yield payload.data.reject();
    }
  } catch (error) {
    yield put(createSessionActions.updateSessionFailed());
    yield payload.data.reject();
  }
}

function* getCategories(resourceId: string) {
  const response: IRestApiResponse = yield call(
    request,
    {
      path: `${API.GET_CATEGORIES.path}?resourceId=${resourceId}&resourceType=${CategoryResourceType.SESSION}`,
      method: API.GET_CATEGORIES.method,
    },
    null,
    true,
  );

  return response;
}

export function* fetchSessionDetailsGenerator({ payload }: ISessionDetailsAction): any {
  try {
    const authUser = yield select(selectAuthUser);
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.GET_FETCH_SESSION_DETAILS.path}/${payload.id}`,
        method: API.GET_FETCH_SESSION_DETAILS.method,
      },
      null,
      true,
    );
    if (response.statusCode === 200) {
      const preferredCurrency = authUser?.attributes?.currency ?? 'AUD';
      const fee = yield call(
        currencyFormat,
        response.data.fee,
        response.data.currency ?? 'AUD',
        preferredCurrency,
      );
      const subscriberFee = yield call(
        currencyFormat,
        response.data.subscriberFee,
        response.data.subscriberCurrency ?? 'AUD',
        preferredCurrency,
      );
      const observerFee = yield call(
        currencyFormat,
        response.data.observerFee,
        response.data.observerCurrency ?? 'AUD',
        preferredCurrency,
      );
      const subscribeObserverFee = yield call(
        currencyFormat,
        response.data.subscribeObserverFee,
        response.data.subscribeObserverCurrency ?? 'AUD',
        preferredCurrency,
      );

      const categories: IRestApiResponse = yield getCategories(payload.id);
      const formattedData = yield {
        ...response.data,
        fee,
        subscriberFee,
        observerFee,
        subscribeObserverFee,
        categories: categories.data,
      };
      yield put(createSessionActions.fetchSessionDetailsSucceeded(formattedData));
      yield payload.resolve();
    } else {
      yield put(createSessionActions.fetchSessionDetailsFailed());
      yield payload.reject();
    }
  } catch (error) {
    yield put(createSessionActions.fetchSessionDetailsFailed());
    yield payload.reject();
  }
}

export function* bulkCancelSessionGenerator({ payload }: BulkCancelSession) {
  try {
    const response: IRestApiResponse = yield call(
      request,
      { path: `${API.POST_BULK_CANCEL_SESSION.path}/${payload.id}`, method: API.POST_BULK_CANCEL_SESSION.method },
      { meetingIds: payload.meetingIds },
    );

    if (response.statusCode === 201) {
      yield put(createSessionActions.bulkCancelSessionSuccess());
      yield payload.resolve();
    } else {
      yield put(createSessionActions.bulkCancelSessionFailed());
      yield payload.reject();
    }
  } catch (error) {
    yield put(createSessionActions.bulkCancelSessionFailed());
    yield payload.reject();
  }
}

export function* saveShareSessionRecordingGenerator({ payload }: SessionRecordingShare) {
  try {
    const response: IRestApiResponse = yield call(
      request,
      { path: `${API.POST_SESSION_RECORDING_SHARE.path}`, method: API.POST_SESSION_RECORDING_SHARE.method },
      payload,
    );

    if (response.statusCode === 201) {
      yield put(createSessionActions.saveShareSessionRecordingSuccess());
      yield payload.resolve();
    } else {
      yield put(createSessionActions.saveShareSessionRecordingFailed());
      yield payload.reject();
    }
  } catch (error) {
    yield put(createSessionActions.saveShareSessionRecordingFailed());
    yield payload.reject();
  }
}

export function* createSessionSaga() {
  yield all([
    takeEvery(createSessionActions.createSession.type, createSessionGenerator),
    takeEvery(createSessionActions.updateSession.type, updateSessionGenerator),
    takeEvery(createSessionActions.fetchSessionDetails.type, fetchSessionDetailsGenerator),
    takeEvery(createSessionActions.fetchGroups.type, fetchGroupsGenerator),
    takeEvery(createSessionActions.fetchSessions.type, fetchSessionsGenerator),
    takeEvery(createSessionActions.fetchModalSessions.type, fetchSessionsGenerator),
    takeEvery(createSessionActions.bulkCancelSession.type, bulkCancelSessionGenerator),
    takeEvery(
      createSessionActions.saveShareSessionRecording.type,
      saveShareSessionRecordingGenerator,
    ),
  ]);
}

export default createSessionSaga;
